import React, {useRef, useState} from 'react';
import {
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {useTranslation} from 'react-i18next';
import Toast from 'react-native-toast-message';
import ArrowRight from '../../assets/icons/arrow-right.svg';
import AuthBrand from '../../components/AuthBrand';
import AuthHeader from '../../components/AuthHeader';
import AuthInput from '../../components/AuthInput';
import AuthButton from '../../components/AuthButton';
import {colors, fonts, radii} from '../../config/theme';
import {updatePassword} from '../../services/auth-service';

// The server's rule (auth/update-password).
const MIN_PASSWORD_LENGTH = 6;
const MAX_PASSWORD_LENGTH = 72;

// Last step of the reset: VerifyOtp (mode 'reset') checked the code and
// passes the resetToken here (it works once, for 15 minutes). On success the user goes back to the email
// login with their address filled in.
const UpdatePassword = ({navigation, route}) => {
  const {t} = useTranslation();
  const insets = useSafeAreaInsets();
  const {email, resetToken} = route.params ?? {};
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const confirmRef = useRef(null);

  // Shown as soon as the second field disagrees, not only on submit.
  const mismatch = confirm.length > 0 && confirm !== password;

  const validate = () => {
    const next = {};
    if (!password) {
      next.password = t('root.Auth.passwordRequired');
    } else if (password.length < MIN_PASSWORD_LENGTH) {
      next.password = t('root.UpdatePassword.tooShort', {
        count: MIN_PASSWORD_LENGTH,
      });
    } else if (password.length > MAX_PASSWORD_LENGTH) {
      next.password = t('root.UpdatePassword.tooLong', {
        count: MAX_PASSWORD_LENGTH,
      });
    }
    if (!confirm) {
      next.confirm = t('root.UpdatePassword.confirmRequired');
    } else if (confirm !== password) {
      next.confirm = t('root.UpdatePassword.mismatch');
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      return;
    }
    setSaving(true);
    try {
      await updatePassword({resetToken, password});
      Toast.show({
        type: 'success',
        text1: t('root.UpdatePassword.success'),
        text2: t('root.UpdatePassword.successBody'),
      });
      // Back to the email login (it stays in the stack) with the email filled in.
      navigation.navigate('LoginEmail', {email});
    } catch (err) {
      console.log('updatePassword error', err);
      Toast.show({
        type: 'error',
        text1: t('root.UpdatePassword.failed'),
        text2: err?.message,
      });
    } finally {
      setSaving(false);
    }
  };

  const onChange = (field, setter) => value => {
    setter(value);
    if (errors[field]) {
      setErrors(prev => ({...prev, [field]: undefined}));
    }
  };

  return (
    <View style={styles.screen}>
      <AuthHeader
        title={t('root.UpdatePassword.title')}
        onBack={() => navigation.goBack()}
      />
      <KeyboardAvoidingView
        style={styles.flex}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <ScrollView
          style={styles.flex}
          contentContainerStyle={[
            styles.scrollContent,
            {paddingBottom: insets.bottom + 24},
          ]}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}>
          <AuthBrand glowId="updatePasswordGlow" />

          <View style={styles.card}>
            <Text style={styles.heading} accessibilityRole="header">
              {t('root.UpdatePassword.heading')}
            </Text>
            <Text style={styles.copy}>{t('root.UpdatePassword.body')}</Text>
            <View style={styles.form}>
              <AuthInput
                variant="filled"
                secure
                required
                label={t('root.UpdatePassword.newPassword')}
                placeholder={t('root.UpdatePassword.newPasswordPlaceholder')}
                value={password}
                onChangeText={onChange('password', setPassword)}
                error={errors.password}
                autoCapitalize="none"
                autoCorrect={false}
                maxLength={MAX_PASSWORD_LENGTH}
                autoComplete="new-password"
                textContentType="newPassword"
                returnKeyType="next"
                submitBehavior="submit"
                onSubmitEditing={() => confirmRef.current?.focus()}
              />
              <AuthInput
                ref={confirmRef}
                variant="filled"
                secure
                required
                label={t('root.UpdatePassword.confirmPassword')}
                placeholder={t('root.UpdatePassword.confirmPasswordPlaceholder')}
                value={confirm}
                onChangeText={onChange('confirm', setConfirm)}
                error={
                  errors.confirm ||
                  (mismatch ? t('root.UpdatePassword.mismatch') : undefined)
                }
                autoCapitalize="none"
                autoCorrect={false}
                maxLength={MAX_PASSWORD_LENGTH}
                autoComplete="new-password"
                textContentType="newPassword"
                returnKeyType="done"
                onSubmitEditing={handleSubmit}
              />
              <AuthButton
                variant="primary"
                title={t('root.UpdatePassword.submit')}
                iconRight={
                  <ArrowRight width={16} height={16} color={colors.onPrimary} />
                }
                loading={saving}
                onPress={handleSubmit}
                style={styles.submit}
              />
            </View>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </View>
  );
};

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: colors.background,
  },
  flex: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    paddingHorizontal: 20,
  },
  card: {
    backgroundColor: colors.surface,
    borderRadius: radii.sheet,
    borderWidth: 1,
    borderColor: colors.borderSubtle,
    padding: 24,
    ...Platform.select({
      ios: {
        shadowColor: colors.primary,
        shadowOffset: {width: 0, height: 8},
        shadowOpacity: 0.07,
        shadowRadius: 18,
      },
      // RN 0.77 draws square elevation under big radii (see theme.js), so
      // Android leans on a firmer border instead.
      default: {borderColor: colors.border},
    }),
  },
  heading: {
    fontFamily: fonts.bold,
    fontSize: 22,
    lineHeight: 28,
    letterSpacing: -0.3,
    color: colors.text,
    textAlign: 'center',
  },
  copy: {
    fontFamily: fonts.regular,
    fontSize: 13.5,
    lineHeight: 21,
    color: colors.textMuted,
    textAlign: 'center',
    marginTop: 8,
    marginBottom: 24,
    paddingHorizontal: 4,
  },
  form: {
    gap: 16,
  },
  submit: {
    marginTop: 4,
  },
});

export default UpdatePassword;
